import { apiService } from './apiService';
import type {
  AiServiceCreditType,
  BalanceWidgetResponse,
  ServiceCardsResponse,
  TeamMembersResponse,
  PersonalCreditsResponse,
  UserCreditLimitDto,
  BatchSetLimitResponse,
  UserBalanceLimitDto,
} from '../../generated-src/client/user-credit-limits/api';

export type {
  AiServiceCreditType,
  BalanceWidgetItem,
  BalanceWidgetResponse,
  ServiceCardItem,
  ServiceCardsResponse,
  TeamMemberItem,
  TeamMembersResponse,
  UserServiceUsage,
  PersonalServiceCard,
  PersonalCreditsResponse,
  UserCreditLimitDto,
  BatchSetLimitResponse,
  UserBalanceLimitDto,
} from '../../generated-src/client/user-credit-limits/api';

class CreditsLimitsService {
  // REL-223: companyId — для супер-админа, чтобы смотреть/менять лимиты выбранной компании.
  // Если не передан — бэк берёт компанию из токена (поведение по умолчанию).
  private withCompany(path: string, companyId?: number): string {
    if (companyId == null) return path;
    const sep = path.includes('?') ? '&' : '?';
    return `${path}${sep}companyId=${companyId}`;
  }

  async getBalanceWidget(companyId?: number): Promise<BalanceWidgetResponse> {
    const res = await apiService.get(this.withCompany('/credits/balance', companyId));
    return res.data;
  }

  async getServiceCards(companyId?: number): Promise<ServiceCardsResponse> {
    const res = await apiService.get(this.withCompany('/credits/services', companyId));
    return res.data;
  }

  async getTeamMembers(params: {
    search?: string;
    page?: number;
    size?: number;
    companyId?: number;
  }): Promise<TeamMembersResponse> {
    const query = new URLSearchParams();
    if (params.search) query.set('search', params.search);
    if (params.page !== undefined) query.set('page', String(params.page));
    if (params.size !== undefined) query.set('size', String(params.size));
    if (params.companyId !== undefined) query.set('companyId', String(params.companyId));
    const qs = query.toString();
    const res = await apiService.get(`/credits/team${qs ? `?${qs}` : ''}`);
    return res.data;
  }

  async setUserLimit(
    userId: number,
    creditType: AiServiceCreditType,
    limit: number | null,
    companyId?: number,
  ): Promise<UserCreditLimitDto> {
    const res = await apiService.put(
      this.withCompany(`/credits/users/${userId}/limits/${creditType}`, companyId),
      { limit },
    );
    return res.data;
  }

  async batchSetLimits(
    userIds: number[],
    creditType: AiServiceCreditType,
    limit: number | null,
    companyId?: number,
  ): Promise<BatchSetLimitResponse> {
    const res = await apiService.post(this.withCompany('/credits/users/limits/batch', companyId), {
      userIds,
      creditType,
      limit,
    });
    return res.data;
  }

  // Л-А: единый личный ₽-лимит на пользователя. limitKopecks = null → «Общий».
  async setUserBalanceLimit(
    userId: number,
    limitKopecks: number | null,
    companyId?: number,
  ): Promise<UserBalanceLimitDto> {
    const res = await apiService.put(
      this.withCompany(`/credits/users/${userId}/limit`, companyId),
      { limitKopecks },
    );
    return res.data;
  }

  async batchSetUserBalanceLimits(
    userIds: number[],
    limitKopecks: number | null,
    companyId?: number,
  ): Promise<BatchSetLimitResponse> {
    const res = await apiService.post(
      this.withCompany('/credits/users/limits/balance/batch', companyId),
      { userIds, limitKopecks },
    );
    return res.data;
  }

  async getMyCredits(): Promise<PersonalCreditsResponse> {
    const res = await apiService.get('/credits/my');
    return res.data;
  }
}

export const creditsLimitsService = new CreditsLimitsService();
